import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, RefreshCw, Pencil, Lock } from 'lucide-react';
import { Card, CardHeader, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { leaveTypesAPI } from '../services/api';
import { confirmToast } from '../lib/confirmToast';

const emptyForm = { name: '', code: '', description: '', is_paid: true, requires_approval: true, is_active: true, color: '' };

const LeaveTypesManagement = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leaveTypesAPI.list();
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load leave types');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      name: row.name, code: row.code, description: row.description || '',
      is_paid: row.is_paid, requires_approval: row.requires_approval,
      is_active: row.is_active, color: row.color || '',
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        // Only mutable fields are sent; code is immutable and never edited.
        await leaveTypesAPI.update(editing.id, {
          name: form.name.trim(), description: form.description || null,
          is_paid: form.is_paid, requires_approval: form.requires_approval,
          is_active: form.is_active, color: form.color || null,
        });
        toast.success('Leave type updated');
      } else {
        await leaveTypesAPI.create({
          name: form.name.trim(), code: form.code.trim() || undefined,
          description: form.description || null, is_paid: form.is_paid,
          requires_approval: form.requires_approval, is_active: form.is_active,
          color: form.color || null,
        });
        toast.success('Leave type created');
      }
      setShowForm(false); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const confirmDelete = (row) => confirmToast({
    message: `Delete "${row.name}"?`,
    description: 'This cannot be undone. Types used by existing requests cannot be deleted — deactivate them instead.',
    confirmLabel: 'Delete',
    destructive: true,
    onConfirm: async () => {
      try { await leaveTypesAPI.remove(row.id); toast.success('Leave type deleted'); load(); }
      catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Leave Types</h2>
          <p className="text-sm text-muted-foreground">Built-in and custom leave types available to employees when applying for leave.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="h-4 w-4 mr-2" />Refresh</Button>
          <Button size="sm" onClick={openCreate}><Plus className="h-4 w-4 mr-2" />New Type</Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-2" />
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Paid</TableHead>
                <TableHead>Approval</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No leave types</TableCell></TableRow>}
              {!loading && rows.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <span className="inline-flex items-center gap-2">
                      {r.is_system && <Lock className="h-3 w-3 text-muted-foreground" title="Built-in" />}
                      {r.name}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{r.code}</TableCell>
                  <TableCell><Badge variant={r.is_paid ? 'default' : 'outline'}>{r.is_paid ? 'Paid' : 'Unpaid'}</Badge></TableCell>
                  <TableCell>{r.requires_approval ? 'Required' : 'Auto'}</TableCell>
                  <TableCell><Badge variant={r.is_active ? 'default' : 'secondary'}>{r.is_active ? 'Active' : 'Inactive'}</Badge></TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(r)}><Pencil className="h-4 w-4" /></Button>
                    {!r.is_system && (
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => confirmDelete(r)}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.is_system ? 'Built-in ' : ''}Leave Type` : 'New Leave Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Sabbatical" />
            </div>
            {!editing && (
              <div>
                <Label>Code <span className="text-xs text-muted-foreground">(optional — auto-derived from name)</span></Label>
                <Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="sabbatical" />
              </div>
            )}
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} />
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_paid} onChange={(e) => setForm((f) => ({ ...f, is_paid: e.target.checked }))} />
                Paid
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.requires_approval} onChange={(e) => setForm((f) => ({ ...f, requires_approval: e.target.checked }))} />
                Requires approval
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />
                Active
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}>{saving ? 'Saving…' : (editing ? 'Update' : 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeaveTypesManagement;
